<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">calculate</span>
          </div>
          <h4 class="title">{{ $t("parcelCalculator.title") }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="pc-form">
            <div class="pc-product">
              <label class="pc-label">{{ $t("parcelCalculator.product") }}</label>
              <v-select
                v-model="product"
                label="label"
                :options="productOptions"
                :filterable="false"
                :placeholder="$t('parcelCalculator.productPlaceholder')"
                :append-to-body="true"
                @search="onProductSearch"
                @input="onProductSelected"
              >
                <template slot="no-options"> {{ $t("parcelCalculator.noProducts") }} </template>
              </v-select>
              <small class="pc-hint">{{ $t("parcelCalculator.productHint") }}</small>
            </div>

            <div class="pc-country">
              <label class="pc-label">{{ $t("parcelCalculator.country") }}</label>
              <v-select v-model="country" label="label" :options="countryOptions" :clearable="false" :placeholder="$t('parcelCalculator.countryPlaceholder')" :append-to-body="true" />
            </div>

            <div class="pc-break"></div>

            <div class="pc-dims">
              <md-field v-for="f in dimensionFields" :key="f.key" class="pc-dim">
                <label>{{ $t("parcelCalculator." + f.key) }}</label>
                <md-input v-model.number="form[f.key]" type="number" min="0" step="0.1" inputmode="decimal"></md-input>
                <span class="md-suffix">{{ f.unit }}</span>
              </md-field>
            </div>

            <md-button class="md-simple pc-tariffs" @click="showTariffs = true">
              <md-icon>{{ canEditTariffs ? "edit" : "visibility" }}</md-icon>
              {{ canEditTariffs ? $t("parcelCalculator.editTariffs") : $t("parcelCalculator.viewTariffs") }}
            </md-button>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div v-if="!inputComplete" class="md-layout-item md-size-100 pc-empty">{{ $t("parcelCalculator.enterValues") }}</div>
    <div v-else-if="calcError" class="md-layout-item md-size-100 pc-error">{{ calcError }}</div>

    <template v-else-if="result">
      <div class="md-layout-item md-size-100 pc-geometry">
        <span
          >{{ $t("parcelCalculator.girth") }}: <b>{{ fmt(result.girth, 0, 2) }} cm</b></span
        >
        <span
          >{{ $t("parcelCalculator.longestSide") }}: <b>{{ fmt(result.longestSide, 0, 2) }} cm</b></span
        >
        <span
          >{{ $t("parcelCalculator.volume") }}: <b>{{ fmt(result.volumeLiters, 0, 2) }} {{ $t("parcelCalculator.liters") }}</b></span
        >
      </div>

      <div v-for="c in result.carriers" :key="c.carrier" class="md-layout-item md-medium-size-100 md-size-50">
        <md-card :class="['pc-carrier', { 'pc-carrier--best': cheapestCarrier === c.carrier }]">
          <md-card-header class="pc-carrier-header">
            <img class="pc-carrier-logo" :src="'/img/logo-' + c.carrier.toLowerCase() + '.png'" :alt="c.carrier" />
            <h4 class="title pc-carrier-title">{{ $t("parcelCalculator.carriers." + c.carrier) }}</h4>
          </md-card-header>
          <md-card-content>
            <div v-if="!c.available" class="pc-unavailable"><md-icon>block</md-icon> {{ $t("parcelCalculator.unavailable." + c.unavailableReason) }}</div>
            <div v-else class="pc-prices">
              <div class="pc-price" :title="money(c.totalRegular)">
                <span class="pc-price-label">{{ $t("parcelCalculator.priceRegular") }}</span>
                <span class="pc-price-value">{{ money(c.totalRegular, 2) }}</span>
              </div>
              <div class="pc-price pc-price--peak" :title="money(c.totalPeak)">
                <span class="pc-price-label">{{ $t("parcelCalculator.pricePeak") }}</span>
                <span class="pc-price-value">{{ money(c.totalPeak, 2) }}</span>
              </div>
            </div>
            <table v-if="c.available" class="pc-table">
              <tbody>
                <tr v-for="row in c.rows" :key="row.key" :class="{ 'pc-zero': row.amount === 0 }">
                  <td>{{ $t("parcelCalculator.rows." + row.key) }}</td>
                  <td class="pc-amount" :title="row.amount === 0 ? $t('parcelCalculator.notApplicable') : ''">{{ row.amount === 0 ? "–" : money(row.amount) }}</td>
                </tr>
                <tr class="pc-peak-row">
                  <td>{{ $t("parcelCalculator.rows.Peak") }}</td>
                  <td class="pc-amount">+ {{ money(c.peakSurcharge) }}</td>
                </tr>
              </tbody>
            </table>
            <ul v-if="c.warnings && c.warnings.length" class="pc-warnings">
              <li v-for="w in c.warnings" :key="w"><md-icon>warning</md-icon> {{ warningText(w) }}</li>
            </ul>
          </md-card-content>
        </md-card>
      </div>
      <div class="md-layout-item md-size-100 pc-hint">{{ $t("parcelCalculator.peakNote") }}</div>
    </template>

    <parcel-tariff-dialog v-if="showTariffs" :initial-year="year" :zones="zones" :can-edit="canEditTariffs" @close="showTariffs = false" @changed="onTariffsChanged" />
  </div>
</template>

<script>
import { hasPermission } from "@/mixins/auth";
import parcelService from "@/services/parcel.service";
import ParcelTariffDialog from "./ParcelTariffDialog.vue";

export default {
  components: { ParcelTariffDialog },
  data() {
    return {
      dimensionFields: [
        { key: "length", unit: "cm" },
        { key: "width", unit: "cm" },
        { key: "height", unit: "cm" },
        { key: "weight", unit: "kg" },
      ],
      form: { length: null, width: null, height: null, weight: null },
      product: null,
      productOptions: [],
      country: null,
      zones: [],
      years: [],
      year: null,
      result: null,
      calcError: "",
      showTariffs: false,
      calcTimer: null,
      searchTimer: null,
      requestId: 0,
    };
  },
  computed: {
    canEditTariffs() {
      return hasPermission("Write", "ParcelCalculator", "EditTariffs");
    },
    countryOptions() {
      const locale = this.$i18n.locale;
      let names = null;
      try {
        names = new Intl.DisplayNames([locale], { type: "region" });
      } catch (e) {
        names = null;
      }
      return this.zones
        .flatMap((z) => z.countries)
        .map((code) => ({ code, label: names ? names.of(code) || code : code }))
        .sort((a, b) => a.label.localeCompare(b.label, locale));
    },
    // Günstigster verfügbarer Dienst (regulärer Preis); ohne Vergleich (nur ein Dienst) oder bei Gleichstand keine Hervorhebung
    cheapestCarrier() {
      if (!this.result) return null;
      const available = this.result.carriers.filter((c) => c.available);
      if (available.length < 2) return null;
      const min = Math.min(...available.map((c) => c.totalRegular));
      const winners = available.filter((c) => c.totalRegular === min);
      return winners.length === 1 ? winners[0].carrier : null;
    },
    inputComplete() {
      return ["length", "width", "height", "weight"].every((k) => Number(this.form[k]) > 0) && !!this.country && this.year != null;
    },
  },
  watch: {
    form: { deep: true, handler: "scheduleCalculate" },
    country: "scheduleCalculate",
    year: "scheduleCalculate",
  },
  created() {
    this.loadMeta();
  },
  beforeDestroy() {
    clearTimeout(this.calcTimer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    loadMeta(keepYear) {
      return parcelService.getMeta().then((meta) => {
        this.zones = meta.zones;
        this.years = meta.years;
        if (!keepYear || !this.years.includes(this.year)) this.year = meta.defaultYear;
        if (!this.country) this.country = this.countryOptions.find((c) => c.code === "DE") || null;
      });
    },
    onProductSearch(search, loading) {
      clearTimeout(this.searchTimer);
      if (!search || search.trim().length < 2) {
        this.productOptions = [];
        return;
      }
      this.searchTimer = setTimeout(() => {
        loading(true);
        parcelService
          .searchProducts(search.trim())
          .then((items) => {
            this.productOptions = items.map((p) => ({ ...p, label: p.name ? `${p.sku} – ${p.name}` : p.sku }));
          })
          .finally(() => loading(false));
      }, 300);
    },
    onProductSelected(product) {
      if (!product) return;
      this.form = { length: product.length, width: product.width, height: product.height, weight: product.weight };
    },
    scheduleCalculate() {
      clearTimeout(this.calcTimer);
      this.calcTimer = setTimeout(this.calculate, 250);
    },
    calculate() {
      if (!this.inputComplete) {
        this.result = null;
        this.calcError = "";
        return;
      }
      const id = ++this.requestId;
      parcelService
        .calculate({ length: this.form.length, width: this.form.width, height: this.form.height, weight: this.form.weight, country: this.country.code, year: this.year })
        .then((result) => {
          if (id !== this.requestId) return;
          this.result = result;
          this.calcError = "";
        })
        .catch((err) => {
          if (id !== this.requestId) return;
          this.result = null;
          const key = `parcelCalculator.errors.${err && err.reason}`;
          this.calcError = this.$te(key) ? this.$t(key) : this.$t("parcelCalculator.errors.generic");
        });
    },
    onTariffsChanged() {
      this.loadMeta(true).then(this.calculate);
    },
    fmt(value, min, max) {
      return new Intl.NumberFormat(this.$i18n.locale, { minimumFractionDigits: min, maximumFractionDigits: max }).format(value);
    },
    money(value, decimals) {
      if (value == null) return "";
      return decimals != null ? `${this.fmt(value, decimals, decimals)} €` : `${this.fmt(value, 2, 5)} €`;
    },
    warningText(code) {
      const key = `parcelCalculator.warnings.${code}`;
      return this.$te(key) ? this.$t(key) : code;
    },
  },
};
</script>

<style scoped>
.pc-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 24px;
}
.pc-product {
  flex: 1 1 320px;
  min-width: 220px;
}
.pc-dims {
  display: flex;
  gap: 16px;
}
.pc-dim {
  width: 110px;
  margin: 0;
}
.pc-dim >>> input {
  min-width: 0;
  width: 100%;
}
.pc-country {
  align-self: flex-start;
  width: 240px;
}
.pc-break {
  flex-basis: 100%;
  height: 0;
}
.pc-product >>> .vs__dropdown-toggle,
.pc-country >>> .vs__dropdown-toggle {
  border: 1px solid #bdbdbd !important;
  border-radius: 4px;
  background: #fff;
  min-height: 38px;
  cursor: pointer;
}
.pc-product >>> .vs__dropdown-toggle:hover,
.pc-country >>> .vs__dropdown-toggle:hover,
.pc-product >>> .vs--open .vs__dropdown-toggle,
.pc-country >>> .vs--open .vs__dropdown-toggle {
  border-color: #7f915a !important;
}
.pc-product >>> .vs__open-indicator,
.pc-country >>> .vs__open-indicator {
  fill: #555;
  transform: scale(1);
}
.pc-label {
  display: block;
  font-size: 12px;
  color: #777;
  margin-bottom: 2px;
}
.pc-hint {
  color: #777;
  font-size: 12px;
}
.pc-empty,
.pc-error {
  padding: 16px;
  color: #777;
}
.pc-error {
  color: #f44336;
}
.pc-geometry {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 10px;
  color: #555;
}
.pc-geometry span {
  background: #fff;
  border-radius: 14px;
  padding: 3px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}
.pc-carrier-header {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pc-carrier-logo {
  height: 44px;
  width: auto;
  object-fit: contain;
  border-radius: 0 !important;
}
.pc-carrier-title {
  margin: 0;
  font-weight: 500;
  color: #555;
}
.pc-prices {
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
}
.pc-price {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 10px 14px;
  border-radius: 6px;
  background: #f1f5e8;
}
.pc-price--peak {
  background: #f3f3f3;
}
.pc-carrier--best {
  box-shadow: 0 0 0 2px #7f915a, 0 1px 4px rgba(0, 0, 0, 0.2);
}
.pc-price-label {
  font-size: 12px;
  color: #666;
}
.pc-price-value {
  font-size: 26px;
  font-weight: 500;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}
.pc-table {
  width: 100%;
  border-collapse: collapse;
}
.pc-table td {
  padding: 5px 4px;
  border-bottom: 1px solid #eee;
}
.pc-amount {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.pc-zero {
  color: #aaa;
}
.pc-total td {
  font-weight: 600;
  border-top: 2px solid #ddd;
}
.pc-peak-row td {
  color: #777;
  border-bottom: 0;
}
.pc-unavailable {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f44336;
}
.pc-warnings {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}
.pc-warnings li {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  color: #b26a00;
  font-size: 13px;
  margin-top: 4px;
}
.pc-warnings .md-icon {
  color: #ff9800;
  font-size: 18px !important;
  min-width: 18px;
  width: 18px;
  height: 18px;
}
.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
</style>
